// Pure helpers used by the team_assist_proposed_action component. Lives in
// its own module so unit tests can exercise them without pulling in the React
// component tree (and its transitive imports — Button, FontAwesome icons,
// etc., which require a DOM-ish environment).

// extractSchemaProperties returns an ordered list of editable field
// descriptors from the destructive tool's JSON Schema. Unrecognised types
// fall back to a single-line text input. Returns [] when no schema is
// present so callers can fall back to the raw-args preview.
export function extractSchemaProperties(inputSchema) {
  if (!inputSchema || typeof inputSchema !== 'object') {
    return [];
  }
  const properties = inputSchema.properties;
  if (!properties || typeof properties !== 'object') {
    return [];
  }
  const requiredSet = new Set(Array.isArray(inputSchema.required) ? inputSchema.required : []);
  return Object.keys(properties).map(name => {
    const spec = properties[name] || {};
    return {
      name,
      label: spec.title || name,
      description: spec.description || '',
      type: inferFieldType(spec),
      enumValues: Array.isArray(spec.enum) ? spec.enum : null,
      required: requiredSet.has(name),
    };
  });
}

export function inferFieldType(spec) {
  if (Array.isArray(spec.enum) && spec.enum.length > 0) {
    return 'enum';
  }
  switch (spec.type) {
    case 'integer':
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'string':
      // The backend only emits the `date-time` JSON Schema format, never
      // `date`, so any other format (including `date`) falls back to text.
      if (spec.format === 'date-time') {
        return 'date';
      }
      return 'text';
    default:
      return 'text';
  }
}

// initialFormValuesFor prefills the form from the LLM's proposed Arguments
// blob (object or JSON-string). Only keys declared in the schema are kept —
// downstream the form value object becomes the executed arguments, and the
// schema is the contract.
export function initialFormValuesFor(args, schemaProperties) {
  const parsed = parseProposedActionArguments(args);
  const initial = {};
  schemaProperties.forEach(field => {
    if (Object.prototype.hasOwnProperty.call(parsed, field.name)) {
      initial[field.name] = parsed[field.name];
    } else {
      initial[field.name] = field.type === 'boolean' ? false : '';
    }
  });
  return initial;
}

export function parseProposedActionArguments(args) {
  if (args == null) {
    return {};
  }
  if (typeof args === 'string') {
    if (!args.trim()) {
      return {};
    }
    try {
      const parsed = JSON.parse(args);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      return {};
    }
  }
  if (typeof args === 'object') {
    return args;
  }
  return {};
}

export function findMissingRequired(formValues, schemaProperties) {
  return schemaProperties
    .filter(field => field.required)
    .filter(field => {
      const value = formValues[field.name];
      if (value == null) return true;
      if (typeof value === 'string') return value.trim() === '';
      return false;
    })
    .map(field => field.name);
}

// formatToolDisplayName renders a destructive external tool name as
// `<action> (<config>)`, where <action> is the substring after the last '-'
// and <config> is the substring before the first '-'. The MCP tool naming
// convention is `<configName>-<source>-<actionName>`, so this surfaces the
// agent-meaningful action name with the configuration that owns it in
// parentheses. Falls back to the raw name when it has no '-' or when either
// side would be empty.
export function formatToolDisplayName(toolName) {
  if (!toolName) {
    return '';
  }
  if (!toolName.includes('-')) {
    return toolName;
  }
  const lastDash = toolName.lastIndexOf('-');
  const firstDash = toolName.indexOf('-');
  const action = toolName.slice(lastDash + 1);
  const config = toolName.slice(0, firstDash);
  if (!action || !config) {
    return toolName;
  }
  return `${action} (${config})`;
}

// formatProposedActionArguments renders the LLM-emitted argument blob as
// pretty-printed JSON text. Always returns text — never HTML — so the
// caller can safely place it inside a <pre> without dangerouslySetInnerHTML.
// Accepts either a plain object or a JSON string (the wire format from
// supernova).
export function formatProposedActionArguments(args) {
  if (args == null) {
    return '';
  }
  if (typeof args === 'string') {
    if (!args.trim()) {
      return '';
    }
    try {
      return JSON.stringify(JSON.parse(args), null, 2);
    } catch (e) {
      return args;
    }
  }
  try {
    return JSON.stringify(args, null, 2);
  } catch (e) {
    return String(args);
  }
}
